import React, { useState, useMemo } from 'react';
import { ALL_GYMS, GYM_STATES, STATE_NAMES } from '../lib/hyroxGymData';

// Tile grid layout — each state positioned in approximate geographic location
// [col, row] on a 12x8 grid
const STATE_GRID = {
  AK: [0, 7], HI: [1, 7],
  WA: [1, 0], OR: [1, 1], CA: [1, 2], NV: [2, 2], ID: [2, 1], MT: [3, 0],
  WY: [3, 1], UT: [2, 3], CO: [3, 3], AZ: [2, 4], NM: [3, 4],
  ND: [4, 0], SD: [4, 1], NE: [4, 2], KS: [4, 3], OK: [4, 4], TX: [4, 5],
  MN: [5, 0], IA: [5, 1], MO: [5, 2], AR: [5, 3], LA: [5, 4], MS: [6, 4],
  WI: [6, 0], IL: [6, 1], IN: [7, 1], MI: [7, 0], OH: [8, 1],
  KY: [7, 2], TN: [7, 3], AL: [7, 4], GA: [8, 4], FL: [8, 5],
  SC: [9, 4], NC: [9, 3], VA: [9, 2], WV: [8, 2],
  PA: [9, 1], NY: [9, 0], NJ: [10, 2], DE: [10, 3], MD: [10, 4], DC: [11, 4],
  CT: [10, 1], RI: [11, 1], MA: [10, 0], VT: [11, 0], NH: [11, 0],
  ME: [11, 0],
};

// Better layout: use a well-known hex/tile grid for US states
const TILE_MAP = [
  //col0  col1   col2   col3   col4   col5   col6   col7   col8   col9   col10  col11
  [null, 'AK',  null,  null,  null,  null,  null,  null,  null,  null,  'ME',  null ], // row 0
  [null, null,  null,  null,  null,  'WI',  null,  null,  null,  'VT',  'NH',  null ], // row 1
  ['WA', 'MT',  'ND',  'MN',  'IL',  'MI',  null,  'NY',  'MA',  'CT',  'RI',  null ], // row 2
  ['OR', 'ID',  'SD',  'IA',  'IN',  'OH',  'PA',  'NJ',  null,  null,  null,  null ], // row 3
  ['CA', 'NV',  'WY',  'NE',  'MO',  'KY',  'WV',  'VA',  'MD',  'DE',  'DC',  null ], // row 4
  [null, 'UT',  'CO',  'KS',  'AR',  'TN',  'NC',  'SC',  null,  null,  null,  null ], // row 5
  [null, 'AZ',  'NM',  'OK',  'LA',  'MS',  'AL',  'GA',  null,  null,  null,  null ], // row 6
  ['HI', null,  null,  'TX',  null,  null,  null,  'FL',  null,  null,  null,  null ], // row 7
];

const GymHeatMap = ({ onStateClick }) => {
  const [hoveredState, setHoveredState] = useState(null);

  const densityData = useMemo(() => {
    const counts = {};
    const cityMap = {};
    ALL_GYMS.forEach(g => {
      counts[g.state] = (counts[g.state] || 0) + 1;
      if (!cityMap[g.state]) cityMap[g.state] = {};
      cityMap[g.state][g.city] = (cityMap[g.state][g.city] || 0) + 1;
    });
    const maxCount = Math.max(...Object.values(counts), 1);
    return { counts, cityMap, maxCount };
  }, []);

  const getColor = (count) => {
    if (!count || count === 0) return '#18181b'; // zinc-900
    const intensity = count / densityData.maxCount;
    if (intensity < 0.08) return '#1a2e0a';
    if (intensity < 0.15) return '#243d10';
    if (intensity < 0.25) return '#2f5215';
    if (intensity < 0.4) return '#3d6b1a';
    if (intensity < 0.6) return '#4f8a22';
    if (intensity < 0.8) return '#65ac2b';
    return '#7fcc35';
  };

  const getBorderColor = (count, isHovered) => {
    if (isHovered) return '#e8ff00';
    if (!count || count === 0) return '#27272a';
    return '#3a5a18';
  };

  const getTopCities = (stateCode) => {
    const cities = densityData.cityMap[stateCode];
    if (!cities) return [];
    return Object.entries(cities)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([city, count]) => `${city} (${count})`);
  };

  // Flatten the tile map to get all state tiles
  const tiles = useMemo(() => {
    const result = [];
    TILE_MAP.forEach((row, rowIdx) => {
      row.forEach((code, colIdx) => {
        if (code) {
          result.push({ code, row: rowIdx, col: colIdx });
        }
      });
    });
    return result;
  }, []);

  const legendSteps = [
    { label: '0', color: '#18181b' },
    { label: '1-3', color: '#1a2e0a' },
    { label: '4-6', color: '#2f5215' },
    { label: '7-10', color: '#3d6b1a' },
    { label: '11-15', color: '#4f8a22' },
    { label: '16+', color: '#7fcc35' },
  ];

  return (
    <div className="mb-8 bg-zinc-900/60 border border-zinc-800 p-4 sm:p-6" data-testid="gym-heat-map">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="text-white font-heading text-lg">HYROX Gym Distribution</h3>
          <p className="text-zinc-500 text-xs mt-1">Click any state to filter gyms below</p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-1" data-testid="heat-map-legend">
          <span className="text-zinc-600 text-[10px] font-heading uppercase tracking-wider mr-1">Low</span>
          {legendSteps.map((s, i) => (
            <div
              key={s.label}
              className="w-5 h-4 sm:w-6 sm:h-5"
              style={{ backgroundColor: s.color, border: '1px solid #333' }}
              title={`${s.label} gyms`}
            />
          ))}
          <span className="text-zinc-600 text-[10px] font-heading uppercase tracking-wider ml-1">High</span>
        </div>
      </div>

      {/* Tile Grid Map */}
      <div className="flex justify-center" data-testid="heat-map-grid">
        <div
          className="grid gap-[3px] sm:gap-1 w-full"
          style={{
            gridTemplateColumns: 'repeat(12, 1fr)',
            gridTemplateRows: 'repeat(8, 1fr)',
            maxWidth: '680px',
          }}
        >
          {TILE_MAP.flat().map((code, idx) => {
            if (!code) {
              // TILE_MAP is a static layout — positional key is stable.
              // eslint-disable-next-line react/no-array-index-key
              return <div key={`empty-tile-${idx}`} className="aspect-square" />;
            }

            const count = densityData.counts[code] || 0;
            const isHovered = hoveredState === code;
            const hasGyms = count > 0;

            return (
              <div
                key={code}
                className="aspect-square relative flex flex-col items-center justify-center cursor-pointer transition-all duration-150 group"
                style={{
                  backgroundColor: isHovered ? '#e8ff00' : getColor(count),
                  border: `1.5px solid ${getBorderColor(count, isHovered)}`,
                  transform: isHovered ? 'scale(1.12)' : 'scale(1)',
                  zIndex: isHovered ? 10 : 1,
                }}
                onMouseEnter={() => setHoveredState(code)}
                onMouseLeave={() => setHoveredState(null)}
                onClick={() => {
                  if (hasGyms && onStateClick) onStateClick(code);
                }}
                data-testid={`map-state-${code.toLowerCase()}`}
              >
                <span
                  className="font-heading text-[10px] sm:text-xs leading-none"
                  style={{ color: isHovered ? '#000' : hasGyms ? '#fff' : '#555' }}
                >
                  {code}
                </span>
                {count > 0 && (
                  <span
                    className="text-[8px] sm:text-[10px] leading-none mt-0.5 font-bold"
                    style={{ color: isHovered ? '#000' : '#e8ff00' }}
                  >
                    {count}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover tooltip */}
      {hoveredState && (
        <div className="mt-3 flex items-center justify-center gap-4 text-center" data-testid="heat-map-tooltip">
          <span className="text-white font-heading text-sm">{STATE_NAMES[hoveredState] || hoveredState}</span>
          <span className="text-[#e8ff00] text-xs font-heading">
            {densityData.counts[hoveredState] || 0} gym{(densityData.counts[hoveredState] || 0) !== 1 ? 's' : ''}
          </span>
          {getTopCities(hoveredState).length > 0 && (
            <span className="text-zinc-500 text-[11px]">{getTopCities(hoveredState).join(' · ')}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default GymHeatMap;
